import type { PropsWithChildren, ReactNode } from 'react'
import { useRef } from 'react'
import { createPortal } from 'react-dom'
import { cx } from 'src/utils/cx'
import { useEmbedded } from 'src/utils/hooks/useEmbedded'
import { Header } from './header'
import styles from './index.module.css'
import { WorkbenchTrackingContext } from './tracking'

export const WORKBENCH_OVERLAY_ID = 'workbench-layout-overlays'

type Props = PropsWithChildren<{
  scene?: string
  menuSlot?: ReactNode
  headerSlot?: ReactNode
  main?: ReactNode
  left?: ReactNode
  right?: ReactNode
  mainProps?: StyleProps
}>

export default function WorkbenchLayout({
  scene,
  headerSlot,
  menuSlot,
  main,
  left,
  right,
  children,
  mainProps,
}: Props) {
  return (
    <WorkbenchTrackingContext.Provider value={{ scene }}>
      <WorkbenchLayoutBase menuSlot={menuSlot} headerSlot={headerSlot}>
        {left && (
          <div
            className="hidden mlg:flex flex-col mlg:overflow-hidden"
            style={{ gridArea: 'left' }}
          >
            {left}
          </div>
        )}
        {main && (
          <main
            className={cx('relative flex flex-col', mainProps?.className)}
            style={{
              gridArea: 'main',
              gridColumnStart: left ? 2 : 1,
              gridColumnEnd: right ? -2 : -1,
              ...mainProps?.style,
            }}
          >
            {main}
          </main>
        )}
        {right && (
          <div
            className="flex flex-col mlg:border-l border-neutral-300 dark:border-neutral-700 mlg:overflow-hidden"
            style={{ gridArea: 'right' }}
          >
            {right}
          </div>
        )}
        {children}
      </WorkbenchLayoutBase>
    </WorkbenchTrackingContext.Provider>
  )
}

type WorkbenchLayoutProps = Omit<Props, 'right' | 'left' | 'main' | 'mainProps'>

/**
 * Works as a base component of WorkbenchLayout
 * Grid area needs to be specified manually
 *
 * @param param0
 * @returns
 */
export function WorkbenchLayoutBase({
  scene,
  menuSlot,
  headerSlot,
  children,
}: WorkbenchLayoutProps) {
  const isEmbedded = useEmbedded()

  if (isEmbedded) {
    return (
      <WorkbenchTrackingContext.Provider value={{ scene }}>
        <WorkbenchLayoutEmbedded>{children}</WorkbenchLayoutEmbedded>
      </WorkbenchTrackingContext.Provider>
    )
  } else {
    return (
      <WorkbenchTrackingContext.Provider value={{ scene }}>
        <WorkbenchLayoutPc menuSlot={menuSlot} headerSlot={headerSlot}>
          {children}
        </WorkbenchLayoutPc>
      </WorkbenchTrackingContext.Provider>
    )
  }
}

function WorkbenchLayoutPc({
  menuSlot,
  headerSlot,
  children,
}: WorkbenchLayoutProps) {
  return (
    <div
      id="workbench-layout"
      className={cx(
        styles['workbench-layout'],
        'mlg:h-screen min-h-screen grid text-sm text-[var(--fg-color)] bg-[var(--bg-color)] overflow-visible mlg:overflow-hidden',
        'relative z-0',
      )}
    >
      <Header
        className="bg-background backdrop-blur-lg border-b border-neutral-100 dark:border-neutral-700"
        style={{ gridArea: 'header' }}
        menuSlot={menuSlot}
      >
        {headerSlot}
      </Header>
      {children}
      <div
        id={WORKBENCH_OVERLAY_ID}
        // The header bar has z-index 30, so we need to make sure the overlays are above it
        className="fixed top-0 left-0 z-40 w-full h-0"
      />
    </div>
  )
}

function WorkbenchLayoutEmbedded({ children }: WorkbenchLayoutProps) {
  return (
    <div
      id="workbench-layout"
      className={cx(
        styles['workbench-layout'],
        'mlg:h-screen min-h-screen grid text-sm text-[var(--fg-color)] bg-[var(--bg-color)] overflow-visible mlg:overflow-hidden',
        'relative z-0',
      )}
    >
      {children}
      <div
        id={WORKBENCH_OVERLAY_ID}
        className="fixed top-0 left-0 z-20 w-full h-0"
      />
    </div>
  )
}

export function WorkbenchLayoutPortal(props: React.PropsWithChildren) {
  const portalContainerRef = useRef<HTMLElement | null>(null)

  // Reduce calling getElementById() in the render function
  if (!portalContainerRef.current) {
    const ele = document.getElementById(WORKBENCH_OVERLAY_ID)
    if (ele) portalContainerRef.current = ele
  }
  if (!portalContainerRef.current) {
    console.warn('Rendering null due to Portal Container not found.')
    return null
  }

  return createPortal(
    props.children,
    document.getElementById(WORKBENCH_OVERLAY_ID)!,
  )
}
